import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../../system/client/src/services/api';
import { useAuth } from '@/context/AuthContext';
import NewToTradeCard from './NewToTradeCard';
import RemindersCard from './RemindersCard';
import PageShell from '../../Components/PageShell';
import StatusSelect from '../../Components/StatusSelect';
import Spinner from '../../Components/Spinner';
import './DashboardPage.css';

const fmtAddress = (t) =>
    [t.address_1, t.address_2, t.post_code].filter(Boolean).join(', ') || 'No address';

const fmtUser = (t) =>
    `${t.first_name || ''} ${t.last_name || ''}`.trim() || 'Unknown user';

const greetingFor = (hour) =>
    hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

const DashboardPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const firstName = user?.firstName?.trim();
    const greeting = greetingFor(new Date().getHours());

    const [savedTraders, setSavedTraders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const data = await api.get('saved-traders?summary=true');
                if (!cancelled) setSavedTraders(data);
            } catch (e) {
                console.error('Error fetching saved traders:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const stats = useMemo(() => {
        const total = savedTraders.length;
        const prospect = savedTraders.filter((t) => t.status === 'Prospect').length;
        const client = savedTraders.filter((t) => t.status === 'Client').length;
        const lapsedClient = savedTraders.filter((t) => t.status === 'Lapsed Client').length;
        const rejected = savedTraders.filter((t) => t.status === 'Rejected').length;
        return { total, prospect, client, lapsedClient, rejected };
    }, [savedTraders]);

    const conversionRate =
        stats.total > 0 ? Math.round((stats.client / stats.total) * 100) : 0;

    const recent = useMemo(
        () =>
            [...savedTraders]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 5),
        [savedTraders],
    );

    const handleStatusChange = async (traderId, newStatus) => {
        try {
            if (newStatus === 'Not Saved') {
                await api.delete(`saved-traders/${traderId}`);
                setSavedTraders((prev) =>
                    prev.filter((t) => t.trader_id !== traderId),
                );
            } else {
                await api.put(`saved-traders/${traderId}/status`, {
                    status: newStatus,
                });
                setSavedTraders((prev) =>
                    prev.map((t) =>
                        t.trader_id === traderId ? { ...t, status: newStatus } : t,
                    ),
                );
            }
        } catch (e) {
            console.error('Error updating status:', e);
        }
    };

    return (
        <PageShell>
            <div className="page-head">
                <span className="kicker">Dashboard</span>
                <h1>
                    {greeting}
                    {firstName ? (
                        <>, <span className="accent">{firstName}.</span></>
                    ) : (
                        '.'
                    )}
                </h1>
                <p>A quick snapshot of what's been happening.</p>
            </div>

            <RemindersCard />

            <div className="kpi-grid">
                <div className="kpi">
                    <span className="kpi-label">Prospects</span>
                    <span className="kpi-value accent">
                        {stats.prospect.toLocaleString()}
                    </span>
                    <span className="kpi-meta">Live opportunities</span>
                </div>
                <div className="kpi">
                    <span className="kpi-label">Clients</span>
                    <span className="kpi-value success">
                        {stats.client.toLocaleString()}
                    </span>
                    <span className="kpi-meta">Active accounts</span>
                </div>
                <div className="kpi">
                    <span className="kpi-label">Lapsed</span>
                    <span className="kpi-value">
                        {stats.lapsedClient.toLocaleString()}
                    </span>
                    <span className="kpi-meta">Re-engage</span>
                </div>
                <div className="kpi">
                    <span className="kpi-label">Rejected</span>
                    <span className="kpi-value">
                        {stats.rejected.toLocaleString()}
                    </span>
                    <span className="kpi-meta">Closed out</span>
                </div>
            </div>

            <NewToTradeCard />

            <section className="section">
                <div className="section-head">
                    <div>
                        <span className="kicker">Recently saved</span>
                        <h2>Latest activity.</h2>
                    </div>
                    <span className="meta">Last 5</span>
                </div>

                {loading ? (
                    <Spinner block label="Loading" />
                ) : recent.length === 0 ? (
                    <div className="empty">
                        <span className="kicker">Nothing yet</span>
                        <p>
                            Search traders and save key accounts to start building your
                            pipeline.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="recent-table-wrap">
                            <table className="fs-table">
                                <thead>
                                    <tr>
                                        <th>Company</th>
                                        <th>Saved by</th>
                                        <th>Saved</th>
                                        <th style={{ textAlign: 'right' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recent.map((t) => (
                                        <tr
                                            key={t.trader_id}
                                            className="clickable"
                                            onClick={() => navigate(`/traders/${t.trader_id}`)}
                                        >
                                            <td className="co">
                                                {t.company_name || 'Unknown company'}
                                                <small>{fmtAddress(t)}</small>
                                            </td>
                                            <td>
                                                {fmtUser(t)}
                                                {t.email && (
                                                    <small
                                                        style={{
                                                            display: 'block',
                                                            color: 'var(--muted)',
                                                            fontFamily: 'var(--font-mono)',
                                                            fontSize: 11,
                                                            marginTop: 3,
                                                        }}
                                                    >
                                                        {t.email}
                                                    </small>
                                                )}
                                            </td>
                                            <td className="date">
                                                {dayjs(t.created_at).format('DD MMM YYYY')}
                                            </td>
                                            <td
                                                style={{ textAlign: 'right' }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <StatusSelect
                                                    traderId={t.trader_id}
                                                    value={t.status}
                                                    onChange={handleStatusChange}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="recent-cards">
                            {recent.map((t) => (
                                <div
                                    key={t.trader_id}
                                    className="trader-card"
                                    onClick={() => navigate(`/traders/${t.trader_id}`)}
                                >
                                    <div className="co">
                                        {t.company_name || 'Unknown company'}
                                    </div>
                                    <div className="meta">{fmtAddress(t)}</div>
                                    <div className="row">
                                        <span className="saved-by">
                                            {fmtUser(t)} ·{' '}
                                            {dayjs(t.created_at).format('DD MMM')}
                                        </span>
                                        <span onClick={(e) => e.stopPropagation()}>
                                            <StatusSelect
                                                traderId={t.trader_id}
                                                value={t.status}
                                                onChange={handleStatusChange}
                                            />
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </section>

            <section className="section">
                <div className="section-head">
                    <div>
                        <span className="kicker">Snapshot</span>
                        <h2>Quick stats.</h2>
                    </div>
                </div>

                <div className="snapshot-grid">
                    <div className="kv-item">
                        <span className="kv-label">Total saved</span>
                        <span className="kv-value">{stats.total.toLocaleString()}</span>
                        <span className="kv-sub">across all stages</span>
                    </div>
                    <div className="kv-item">
                        <span className="kv-label">Conversion</span>
                        <span className="kv-value">{conversionRate}%</span>
                        <div className="kv-bar">
                            <span style={{ width: `${conversionRate}%` }} />
                        </div>
                        <span className="kv-sub">prospects → clients</span>
                    </div>
                    <div className="kv-item">
                        <span className="kv-label">Active pipeline</span>
                        <span className="kv-value">
                            {(stats.prospect + stats.client).toLocaleString()}
                        </span>
                        <span className="kv-sub">prospects + clients</span>
                    </div>
                </div>
            </section>
        </PageShell>
    );
};

export default DashboardPage;
